import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaDatabase, FaChartLine, FaShieldAlt, FaServer, FaGlobe } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import "../../styles/services/solutions/blockchain.css";

const BlockchainSolutions = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const featuresData = [
    {
      title: 'Smart Contract Development',
      description: 'Custom smart contracts for automated business processes, secure transactions, and transparent record-keeping.',
      icon: <FaDatabase className="feature-icon" />,
    },
    {
      title: 'Supply Chain Tracking',
      description: 'End-to-end visibility and traceability solutions to optimize logistics and verify product authenticity.',
      icon: <FaChartLine className="feature-icon" />,
    },
    {
      title: 'Tokenization Solutions',
      description: 'Create digital assets and implement token-based ecosystems for various business applications.',
      icon: <FaShieldAlt className="feature-icon" />,
    },
    {
      title: 'Private Blockchain Networks',
      description: 'Custom blockchain implementations for enterprises requiring secure, permissioned environments.',
      icon: <FaServer className="feature-icon" />,
    },
    {
      title: 'DeFi Applications',
      description: 'Decentralized finance solutions including lending platforms, exchanges, and payment systems.',
      icon: <FaGlobe className="feature-icon" />,
    },
  ];

  const caseStudies = [
    {
      title: 'Global Supply Chain Transformation',
      description: 'How we helped a multinational corporation reduce fraud by 85% and improve shipment tracking accuracy to 99.8% using blockchain technology.',
      image: 'https://via.placeholder.com/600x400',
    },
    {
      title: 'Secure Medical Records Platform',
      description: 'Our blockchain solution enabled a healthcare provider to securely share patient data across institutions while maintaining HIPAA compliance.',
      image: 'https://via.placeholder.com/600x400',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="blockchain-solutions-page"
    >
      {/* Hero Section */}
      <div className="hero-section1">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1>Advanced Blockchain Solutions</h1>
                <p className="lead">Transforming industries through secure, transparent, and efficient blockchain implementations</p>
                <Button variant="primary" size="lg" className="mt-4 hero-button">
                  Schedule a Consultation
                </Button>
              </motion.div>
            </Col>
            <Col lg={6} md={12}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hero-image-container"
              >
                <img
                  src="https://myacademyimages.s3.eu-north-1.amazonaws.com/block.png"
                  alt="Blockchain"
                  className="img-fluid hero-image"
                />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Services Section */}
      <section className="services-section" ref={ref}>
        <Container>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="text-center mb-5"
          >
            <motion.h2 variants={itemVariants} className="section-title">Our Blockchain Services</motion.h2>
            <motion.p variants={itemVariants} className="section-subtitle">
              Cutting-edge blockchain implementations for finance, supply chain, and data security
            </motion.p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <Row>
              {featuresData.map((feature, index) => (
                <Col lg={4} md={6} sm={12} key={index} className="mb-4">
                  <motion.div variants={itemVariants}>
                    <Card className="feature-card h-100">
                      <Card.Body>
                        <div className="icon-container mb-4">
                          {feature.icon}
                        </div>
                        <Card.Title>{feature.title}</Card.Title>
                        <Card.Text>{feature.description}</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Technology Section */}
      <section className="technology-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2>Our Blockchain Technology Stack</h2>
                <p>
                  We leverage the most robust and scalable blockchain technologies to create
                  custom solutions tailored to your business needs. Our expert team has deep
                  experience with:
                </p>
                <ul className="tech-list">
                  <li>Ethereum and EVM-compatible chains</li>
                  <li>Hyperledger Fabric for enterprise applications</li>
                  <li>Solidity smart contract development</li>
                  <li>IPFS for decentralized storage</li>
                  <li>Zero-knowledge proof implementations</li>
                  <li>Layer 2 scaling solutions</li>
                </ul>
                <Button variant="outline-primary" className="mt-3">Explore Our Technology</Button>
              </motion.div>
            </Col>
            <Col lg={6} md={12}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <img
                  src="https://myacademyimages.s3.eu-north-1.amazonaws.com/blockchain.webp"
                  alt="Blockchain Technology"
                  className="img-fluid tech-image"
                />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Case Studies */}
      <section className="case-studies-section">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-5"
          >
            <h2 className="section-title">Success Stories</h2>
            <p className="section-subtitle">
              Real-world blockchain implementations that transformed businesses
            </p>
          </motion.div>

          <Row>
            {caseStudies.map((study, index) => (
              <Col lg={6} md={12} className="mb-4" key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <Card className="case-study-card">
                    <Card.Img variant="top" src={study.image} />
                    <Card.Body>
                      <Card.Title>{study.title}</Card.Title>
                      <Card.Text>{study.description}</Card.Text>
                      <Button variant="link" className="p-0">Read Case Study →</Button>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>

          <div className="text-center mt-5">
            <Button variant="outline-primary" size="lg">View All Case Studies</Button>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content text-center"
          >
            <h2>Ready to Transform Your Business with Blockchain?</h2>
            <p className="lead">
              Our expert team is ready to help you implement secure, scalable blockchain solutions.
            </p>
            <Button variant="primary" size="lg" className="mt-4">
              Get Started Today
            </Button>
          </motion.div>
        </Container>
      </section>
    </motion.div>
  );
};

export default BlockchainSolutions;